import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { TID } from "@/constants/testIds";

const GOOGLE_AUTH_URL = "https://auth.emergentagent.com/";

export default function LoginModal() {
    const { closeModal, login, register, onAuthenticated } = useAuth();
    const [tab, setTab] = useState("existing"); // 'existing' | 'new'
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [loading, setLoading] = useState(false);
    const [err, setErr] = useState("");

    // ESC to close
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") closeModal();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [closeModal]);

    async function submit(e) {
        e?.preventDefault();
        setErr("");
        setLoading(true);
        try {
            if (tab === "existing") {
                await login(email.trim(), password);
                toast.success("Welcome back!");
            } else {
                await register({
                    name: name.trim(),
                    email: email.trim(),
                    password,
                    phone: phone.trim() || undefined,
                });
                toast.success("Account created.");
            }
            onAuthenticated();
        } catch (e2) {
            const detail = e2?.response?.data?.detail;
            setErr(typeof detail === "string" ? detail : "Something went wrong.");
        } finally {
            setLoading(false);
        }
    }

    function demoFill(role) {
        setTab("existing");
        if (role === "admin") {
            setEmail("sushenherbs@gmail.com");
            setPassword("sushen@123");
        } else if (role === "customer") {
            setEmail("customer@vedaheal.com");
            setPassword("customer123");
        } else if (role === "doctor") {
            setEmail("ananya@vedaheal.com");
            setPassword("doctor123");
        }
    }

    function google() {
        const redirect = window.location.origin + "/";
        window.location.href =
            GOOGLE_AUTH_URL + "?redirect=" + encodeURIComponent(redirect);
    }

    return (
        <div
            data-testid={TID.loginModal}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-veda-inverse/40 backdrop-blur-sm sm:items-center animate-fade-in"
            onClick={(e) => {
                if (e.target === e.currentTarget) closeModal();
            }}
        >
            <div className="relative w-full max-w-md border-t border-veda-border bg-veda-surface p-6 sm:border sm:p-8">
                <button
                    data-testid={TID.modalClose}
                    onClick={closeModal}
                    className="absolute right-4 top-4 p-1 text-veda-muted hover:text-veda-onSurface"
                    aria-label="Close"
                >
                    <X size={18} />
                </button>

                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                    Sushen Herbs
                </p>
                <h2 className="font-display mt-1 text-3xl text-veda-onSurface">
                    Welcome back
                </h2>
                <p className="mt-1 text-xs text-veda-muted">
                    Sign in to book Vaidyas, place orders, and chat with your
                    practitioner.
                </p>

                {/* Tabs */}
                <div className="mt-6 grid grid-cols-2 border border-veda-border">
                    <button
                        data-testid={TID.modalTabCustomer}
                        onClick={() => {
                            setTab("existing");
                            setErr("");
                        }}
                        className={`py-2 text-xs font-semibold uppercase tracking-[0.16em] ${
                            tab === "existing"
                                ? "bg-veda-inverse text-veda-onInverse"
                                : "text-veda-onSurface"
                        }`}
                    >
                        Sign in
                    </button>
                    <button
                        data-testid={TID.modalTabNew}
                        onClick={() => {
                            setTab("new");
                            setErr("");
                        }}
                        className={`py-2 text-xs font-semibold uppercase tracking-[0.16em] ${
                            tab === "new"
                                ? "bg-veda-inverse text-veda-onInverse"
                                : "text-veda-onSurface"
                        }`}
                    >
                        New customer
                    </button>
                </div>

                <form onSubmit={submit} className="mt-5 flex flex-col gap-3">
                    {tab === "new" && (
                        <input
                            type="text"
                            placeholder="Full name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    )}
                    <input
                        data-testid={TID.modalEmailInput}
                        type="email"
                        placeholder="Email"
                        required
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />
                    <input
                        data-testid={TID.modalPasswordInput}
                        type="password"
                        placeholder="Password"
                        required
                        autoComplete={tab === "new" ? "new-password" : "current-password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                    />
                    {tab === "new" && (
                        <input
                            type="tel"
                            placeholder="Phone (optional)"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className="border border-veda-border bg-veda-surface px-3 py-2.5 text-sm focus:border-veda-brand focus:outline-none"
                        />
                    )}
                    {err && <p className="text-xs text-veda-error">{err}</p>}
                    <button
                        type="submit"
                        data-testid={TID.modalLoginSubmit}
                        disabled={loading}
                        className="inline-flex items-center justify-center gap-2 bg-veda-brand px-4 py-2.5 text-sm font-semibold uppercase tracking-[0.14em] text-white hover:bg-veda-brand/95 disabled:opacity-70"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        {tab === "existing" ? "Sign in" : "Create account"}
                    </button>
                </form>

                <div className="my-5 flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-veda-muted">
                    <div className="h-px flex-1 bg-veda-divider" />
                    or
                    <div className="h-px flex-1 bg-veda-divider" />
                </div>

                <button
                    data-testid={TID.modalGoogleSignin}
                    onClick={google}
                    className="flex w-full items-center justify-center gap-2 border border-veda-border bg-veda-surface px-4 py-2.5 text-sm font-medium text-veda-onSurface hover:bg-veda-surface2"
                >
                    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
                        <path
                            fill="#EA4335"
                            d="M12 10.2v3.9h5.5c-.24 1.4-1.65 4.1-5.5 4.1-3.31 0-6-2.74-6-6.1s2.69-6.1 6-6.1c1.88 0 3.14.79 3.86 1.47l2.64-2.55C16.9 3.34 14.66 2.4 12 2.4 6.98 2.4 2.9 6.5 2.9 12s4.08 9.6 9.1 9.6c5.25 0 8.72-3.69 8.72-8.88 0-.6-.07-1.06-.15-1.52H12z"
                        />
                    </svg>
                    Continue with Google
                </button>

                <div className="mt-6">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-veda-muted">
                        Demo accounts
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <button
                            data-testid={TID.modalDemoAdmin}
                            onClick={() => demoFill("admin")}
                            className="rounded-pill border border-veda-border px-3 py-1 text-[11px] text-veda-onSurface hover:bg-veda-surface2"
                        >
                            Admin
                        </button>
                        <button
                            data-testid={TID.modalDemoCustomer}
                            onClick={() => demoFill("customer")}
                            className="rounded-pill border border-veda-border px-3 py-1 text-[11px] text-veda-onSurface hover:bg-veda-surface2"
                        >
                            Customer
                        </button>
                        <button
                            data-testid={TID.modalDemoDoctor}
                            onClick={() => demoFill("doctor")}
                            className="rounded-pill border border-veda-border px-3 py-1 text-[11px] text-veda-onSurface hover:bg-veda-surface2"
                        >
                            Doctor
                        </button>
                    </div>
                </div>

                <div className="mt-6 text-center text-xs text-veda-muted">
                    A practitioner?{" "}
                    <Link
                        to="/auth/register-doctor"
                        data-testid={TID.modalDoctorSignup}
                        onClick={closeModal}
                        className="text-veda-brand underline underline-offset-2"
                    >
                        Apply to join as a Vaidya
                    </Link>
                </div>
            </div>
        </div>
    );
}
